/**
 * Curva ABC e cronograma físico-financeiro de desembolso do orçamento.
 * Valores com BDI (preço de contrato): serviços e mob./desmob./adm. local com o
 * BDI de serviços; itens da seção de materiais com o BDI de materiais.
 */
import { COMPOSICOES_CANTEIRO } from "./canteiro";
import type { ResumoOrcamento, TipoComposicao } from "./tipos";

// ------------------------------------------------------------------ curva ABC
export type ClasseAbc = "A" | "B" | "C";

export interface LinhaAbc {
  posicao: number;
  codigo: string;
  descricao: string;
  unidade: string | null;
  quantidade: number;
  /** Preço unitário com BDI. */
  preco_unitario: number;
  /** Total com BDI. */
  total: number;
  /** Participação no total do orçamento (%). */
  percentual: number;
  /** Participação acumulada (%). */
  acumulado: number;
  classe: ClasseAbc;
}

export interface CurvaAbc {
  linhas: LinhaAbc[];
  total: number;
  limites: { a: number; b: number };
  resumo: Record<ClasseAbc, { itens: number; total: number; percentual: number }>;
}

function fatoresBdi(resumo: ResumoOrcamento) {
  return {
    servicos: 1 + (resumo.bdi_servicos_pct ?? 0) / 100,
    materiais: 1 + (resumo.bdi_materiais_pct ?? 0) / 100,
  };
}

/**
 * Itens ordenados do maior para o menor valor. O mesmo código com o mesmo
 * preço em mais de uma linha (seções diferentes) entra uma vez só, somando a
 * quantidade. Classe pelo acumulado ANTES do item: A até `a`% do valor, B até
 * `b`%, C o restante (o item que cruza o limite fica na classe anterior).
 */
export function calcularCurvaAbc(resumo: ResumoOrcamento, limites = { a: 80, b: 95 }): CurvaAbc {
  const bdi = fatoresBdi(resumo);
  const grupos = new Map<string, Omit<LinhaAbc, "posicao" | "percentual" | "acumulado" | "classe">>();
  const somar = (codigo: string, descricao: string, unidade: string | null, quantidade: number, preco: number) => {
    const chave = `${codigo}|${preco.toFixed(6)}`;
    const g = grupos.get(chave);
    if (g) {
      g.quantidade += quantidade;
      g.total += quantidade * preco;
    } else grupos.set(chave, { codigo, descricao, unidade, quantidade, preco_unitario: preco, total: quantidade * preco });
  };
  for (const it of resumo.itens) {
    const fator = it.tipo_bdi === "materiais" ? bdi.materiais : bdi.servicos;
    somar(it.codigo_snapshot, it.descricao_snapshot, it.unidade_snapshot, it.quantidade, it.preco_unitario * fator);
  }
  for (const tipo of Object.keys(COMPOSICOES_CANTEIRO) as TipoComposicao[]) {
    const valor = resumo.totais_especiais[tipo];
    if (valor > 0) somar(COMPOSICOES_CANTEIRO[tipo].codigo, COMPOSICOES_CANTEIRO[tipo].descricao, "UN", 1, valor * bdi.servicos);
  }

  const ordenadas = [...grupos.values()].filter((g) => g.total > 0).sort((x, y) => y.total - x.total);
  const total = ordenadas.reduce((s, g) => s + g.total, 0);
  const resumoClasses: CurvaAbc["resumo"] = {
    A: { itens: 0, total: 0, percentual: 0 },
    B: { itens: 0, total: 0, percentual: 0 },
    C: { itens: 0, total: 0, percentual: 0 },
  };
  let acumulado = 0;
  const linhas = ordenadas.map((g, i) => {
    const percentual = total > 0 ? (g.total / total) * 100 : 0;
    const anterior = acumulado;
    acumulado += percentual;
    const classe: ClasseAbc = anterior < limites.a ? "A" : anterior < limites.b ? "B" : "C";
    resumoClasses[classe].itens += 1;
    resumoClasses[classe].total += g.total;
    resumoClasses[classe].percentual += percentual;
    return { ...g, posicao: i + 1, percentual, acumulado: Math.min(acumulado, 100), classe };
  });
  return { linhas, total, limites, resumo: resumoClasses };
}

// ---------------------------------------------------- cronograma de desembolso
/** Função de distribuição acumulada da normal padrão (erf de Abramowitz-Stegun 7.1.26). */
function phi(x: number): number {
  const z = Math.abs(x) / Math.SQRT2;
  const t = 1 / (1 + 0.3275911 * z);
  const erf = 1 - ((((1.061405429 * t - 1.453152027) * t + 1.421413741) * t - 0.284496736) * t + 0.254829592) * t * Math.exp(-z * z);
  return x >= 0 ? (1 + erf) / 2 : (1 - erf) / 2;
}

/**
 * Pesos mensais de uma curva normal (gaussiana) centrada no meio do prazo,
 * com desvio padrão de 1/6 do prazo (±3σ cobrem a obra toda): a área sob a
 * curva em cada mês, normalizada para somar 1. Acumulada, dá a curva S.
 */
export function pesosGaussianos(meses: number): number[] {
  const n = Math.max(1, Math.floor(meses));
  if (n === 1) return [1];
  const media = n / 2;
  const desvio = n / 6;
  const brutas = Array.from({ length: n }, (_, i) => phi((i + 1 - media) / desvio) - phi((i - media) / desvio));
  // Média com o mês espelhado: a aproximação do erf deixa resíduo de ~1e-9.
  const areas = brutas.map((a, i) => (a + brutas[n - 1 - i]) / 2);
  const soma = areas.reduce((s, a) => s + a, 0);
  return areas.map((a) => a / soma);
}

export type TipoLinhaCronograma = "mobilizacao" | "servico" | "administracao_local" | "desmobilizacao";

export interface LinhaCronograma {
  tipo: TipoLinhaCronograma;
  numero: string;
  descricao: string;
  /** Total com BDI. */
  total: number;
  /** Valor por mês (com BDI). */
  meses: number[];
}

export interface Cronograma {
  prazo: number;
  linhas: LinhaCronograma[];
  total: number;
  /** Desembolso de cada mês (soma das linhas). */
  mensal: number[];
  acumulado: number[];
  percentual_mensal: number[];
  percentual_acumulado: number[];
}

/**
 * Cronograma físico-financeiro. Critérios (orientação do TCU, Acórdão
 * 2.622/2013-Plenário):
 *  - serviços (cada seção do orçamento) seguem a gaussiana — curva S;
 *  - mobilização paga de uma vez na 1ª medição;
 *  - desmobilização paga na última medição;
 *  - administração local paga proporcionalmente à execução dos serviços no
 *    mês (não em parcelas fixas), para não ser antecipada nem paga em atraso.
 */
export function calcularCronograma(resumo: ResumoOrcamento, prazoMeses: number): Cronograma {
  const prazo = Math.max(1, Math.floor(prazoMeses));
  const pesos = pesosGaussianos(prazo);
  const bdi = fatoresBdi(resumo);
  const distribuir = (total: number, w: number[]) => w.map((p) => total * p);
  const somenteMes = (total: number, mes: number) => Array.from({ length: prazo }, (_, i) => (i === mes ? total : 0));

  const valorItem = (it: ResumoOrcamento["itens"][number]) => it.total * (it.tipo_bdi === "materiais" ? bdi.materiais : bdi.servicos);
  // Serviços agrupados pela seção principal (subseções somam na seção-mãe).
  const raizDe = new Map<number, number>();
  for (const sec of resumo.secoes) raizDe.set(sec.id, sec.pai_id ?? sec.id);
  const etapas: { numero: string; descricao: string; total: number }[] = [];
  for (const sec of resumo.secoes.filter((s) => s.nivel === 1)) {
    const total = resumo.itens.filter((it) => it.secao_id != null && raizDe.get(it.secao_id) === sec.id).reduce((s, it) => s + valorItem(it), 0);
    if (total > 0) etapas.push({ numero: sec.numero, descricao: sec.titulo, total });
  }
  const semSecao = resumo.itens.filter((it) => it.secao_id == null || !raizDe.has(it.secao_id)).reduce((s, it) => s + valorItem(it), 0);
  if (semSecao > 0) etapas.push({ numero: "", descricao: resumo.secoes.length ? "Itens sem seção" : "Serviços do orçamento", total: semSecao });

  const linhas: LinhaCronograma[] = [];
  const mob = resumo.totais_especiais.mobilizacao * bdi.servicos;
  if (mob > 0) linhas.push({ tipo: "mobilizacao", numero: "", descricao: "Mobilização e instalação do canteiro", total: mob, meses: somenteMes(mob, 0) });
  for (const e of etapas) linhas.push({ tipo: "servico", ...e, meses: distribuir(e.total, pesos) });

  // Adm. local acompanha a execução financeira dos serviços (sem mob./desmob.).
  const adm = resumo.totais_especiais.administracao_local * bdi.servicos;
  if (adm > 0) {
    const servicosMes = Array.from({ length: prazo }, (_, i) => linhas.filter((l) => l.tipo === "servico").reduce((s, l) => s + l.meses[i], 0));
    const totalServicos = servicosMes.reduce((s, v) => s + v, 0);
    const proporcao = totalServicos > 0 ? servicosMes.map((v) => v / totalServicos) : pesos;
    linhas.push({ tipo: "administracao_local", numero: "", descricao: "Administração local", total: adm, meses: distribuir(adm, proporcao) });
  }
  const desmob = resumo.totais_especiais.desmobilizacao * bdi.servicos;
  if (desmob > 0) linhas.push({ tipo: "desmobilizacao", numero: "", descricao: "Desmobilização do canteiro", total: desmob, meses: somenteMes(desmob, prazo - 1) });

  const mensal = Array.from({ length: prazo }, (_, i) => linhas.reduce((s, l) => s + l.meses[i], 0));
  const total = linhas.reduce((s, l) => s + l.total, 0);
  const acumulado: number[] = [];
  mensal.reduce((s, v, i) => (acumulado[i] = s + v), 0);
  const pct = (v: number) => (total > 0 ? (v / total) * 100 : 0);
  return {
    prazo,
    linhas,
    total,
    mensal,
    acumulado,
    percentual_mensal: mensal.map(pct),
    percentual_acumulado: acumulado.map(pct),
  };
}
